/**
 * Shared utility functions
 */
/**
 * Validates an email address format
 */
export function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}
/**
 * Formats a date string to a readable format
 */
export function formatDate(dateString, locale = 'en-US') {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}
/**
 * Formats a date string to include time
 */
export function formatDateTime(dateString, locale = 'en-US') {
    const date = new Date(dateString);
    return date.toLocaleString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
/**
 * Calculates days between two dates
 */
export function daysBetween(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
/**
 * Checks if a date is in the past
 */
export function isDatePast(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    return date < now;
}
/**
 * Gets user full name
 */
export function getUserFullName(firstName, lastName) {
    return `${firstName} ${lastName}`.trim();
}
/**
 * Slugifies a string (converts to URL-friendly format)
 */
export function slugify(text) {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
/**
 * Truncates text to a specified length
 */
export function truncate(text, maxLength, suffix = '...') {
    if (text.length <= maxLength) {
        return text;
    }
    return text.slice(0, maxLength - suffix.length) + suffix;
}
/**
 * Generates a random ID (for temporary use, should use proper UUID in production)
 */
export function generateId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
/**
 * Deep clones an object
 */
export function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}
/**
 * Checks if an object is empty
 */
export function isEmpty(obj) {
    return Object.keys(obj).length === 0;
}
// ============================================================================
// Hiring Pipeline Helpers
// ============================================================================
const STAGE_ORDER = ['application', 'interview', 'offer', 'onboarding_docs', 'complete'];
const TERMINAL_STAGES_SET = new Set(['complete', 'rejected', 'withdrawn']);
/**
 * Checks whether a stage transition is valid.
 * Rules:
 *  - Cannot transition out of a terminal stage (complete, rejected, withdrawn).
 *  - Can move to 'rejected' or 'withdrawn' from any non-terminal stage.
 *  - Otherwise, can only move forward one step in the pipeline.
 */
export function isValidStageTransition(from, to) {
    if (from === to)
        return false;
    if (TERMINAL_STAGES_SET.has(from))
        return false;
    if (to === 'rejected' || to === 'withdrawn')
        return true;
    const fromIndex = STAGE_ORDER.indexOf(from);
    const toIndex = STAGE_ORDER.indexOf(to);
    if (fromIndex === -1 || toIndex === -1)
        return false;
    return toIndex === fromIndex + 1;
}
/**
 * Returns the label for a pipeline stage.
 */
export function getStageLabel(stage) {
    const labels = {
        application: 'Application',
        interview: 'Interview',
        offer: 'Offer',
        agreement_signing: 'Agreement Signing',
        background_check: 'Background Check',
        onboarding_docs: 'Onboarding Docs',
        complete: 'Complete',
        rejected: 'Rejected',
        withdrawn: 'Withdrawn',
    };
    return labels[stage] ?? stage;
}
//# sourceMappingURL=index.js.map